const META = /[.+?^${}()|[\]\\]/;

/**
 * any pattern that matches `https://example.invalid/` is silently
 * dropped by the proxy and would result in auth never being applied.
 */
export function isTriviallyMatchingPattern(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  const anchored = trimmed.startsWith("^") ? trimmed : `^${trimmed}`;
  let regex: RegExp;
  try {
    regex = new RegExp(anchored);
  } catch {
    return false;
  }
  return regex.test("https://example.invalid/");
}

/**
 * The proxy compiles patterns with Rust’s regex crate, which has no
 * lookarounds or backreferences; it silently drops patterns it can’t compile.
 */
export function urlPatternError(raw: string): string | null {
  const pattern = raw.trim();
  if (!pattern) return null;
  if (/\(\?<?[=!]|\\[1-9]/.test(pattern)) {
    return "Lookarounds and backreferences aren’t supported.";
  }
  try {
    new RegExp(pattern);
  } catch {
    return "Invalid regular expression.";
  }
  return null;
}

function splitTopLevelAlternation(pattern: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let inClass = false;
  let current = "";
  for (let i = 0; i < pattern.length; i++) {
    const c = pattern[i]!;
    if (c === "\\") {
      current += c + (pattern[i + 1] ?? "");
      i++;
      continue;
    }
    if (inClass) {
      current += c;
      if (c === "]") inClass = false;
      continue;
    }
    if (c === "[") {
      inClass = true;
      current += c;
    } else if (c === "(") {
      depth++;
      current += c;
    } else if (c === ")") {
      depth--;
      current += c;
    } else if (c === "|" && depth === 0) {
      parts.push(current);
      current = "";
    } else {
      current += c;
    }
  }
  parts.push(current);
  return parts;
}

function unwrapNonCapturingGroup(branch: string): string | null {
  if (!branch.startsWith("(?:")) return null;
  let depth = 0;
  let inClass = false;
  for (let i = 0; i < branch.length; i++) {
    const c = branch[i]!;
    if (c === "\\") {
      i++;
      continue;
    }
    if (inClass) {
      if (c === "]") inClass = false;
      continue;
    }
    if (c === "[") inClass = true;
    else if (c === "(") depth++;
    else if (c === ")") {
      depth--;
      if (depth === 0)
        return i === branch.length - 1 ? branch.slice(3, i) : null;
    }
  }
  return null;
}

function flattenBranches(pattern: string): string[] {
  const trimmed = pattern.trim();
  if (!trimmed) return [];
  const parts = splitTopLevelAlternation(trimmed);
  if (parts.length > 1) return parts.flatMap(flattenBranches);
  const unanchored = trimmed.startsWith("^") ? trimmed.slice(1) : trimmed;
  const unwrapped = unwrapNonCapturingGroup(unanchored);
  if (unwrapped !== null) return flattenBranches(unwrapped);
  const leaf = unanchored.trim();
  return leaf ? [leaf] : [];
}

/**
 * The proxy compiles a pattern by prepending a single `^`, so every branch of a
 * top-level alternation must carry its own `^` or it matches any URL that merely
 * *contains* the branch (F-11). Flatten nested/duplicate alternations and anchor
 * each branch so repeated widening stays bounded and can't lose its anchor.
 */
export function anchorAlternation(pattern: string): string {
  const seen = new Set<string>();
  const branches = flattenBranches(pattern).filter((b) => {
    if (seen.has(b)) return false;
    seen.add(b);
    return true;
  });
  if (branches.length === 0) return "";
  if (branches.length === 1) return `^${branches[0]}`;
  return branches.map((b) => `^(?:${b})`).join("|");
}

export function widenUrlPattern(
  existing: string | null | undefined,
  addition: string
): string {
  return anchorAlternation(
    [existing ?? "", addition].filter(Boolean).join("|")
  );
}

export function domainToRegex(input: string | undefined): string {
  let s = (input ?? "").trim();
  if (!s) return ".*";
  s = s.replace(/^https?:\/\//, "");

  let body = "";
  for (const ch of s) {
    if (ch === "*") body += ".*";
    else if (META.test(ch)) body += `\\${ch}`;
    else body += ch;
  }

  while (body.endsWith("/")) body = body.slice(0, -1);
  if (!body.endsWith(".*")) body += "/.*";

  return `https://${body}`;
}

export function regexToDomain(regex: string): string | null {
  if (!regex || regex === ".*") return null;
  if (!regex.startsWith("https://")) return null;

  let body: string;
  let trailingStar: boolean;
  if (regex.endsWith("/.*")) {
    body = regex.slice(8, -3);
    trailingStar = false;
  } else if (regex.endsWith(".*")) {
    body = regex.slice(8, -2);
    trailingStar = true;
  } else {
    return null;
  }

  let out = "";
  for (let i = 0; i < body.length; i++) {
    const c = body[i]!;
    if (c === "\\" && i + 1 < body.length) {
      out += body[++i];
      continue;
    }
    if (c === "." && body[i + 1] === "*") {
      out += "*";
      i++;
      continue;
    }
    if (META.test(c)) return null;
    out += c;
  }

  if (!out) return null;
  return trailingStar ? `${out}*` : out;
}
